import React from 'react';
import DatePicker, { registerLocale } from "react-datepicker";
import { th } from 'date-fns/locale/th';
import "react-datepicker/dist/react-datepicker.css";

registerLocale('th', th);

const THAI_MONTHS = [
    'มกราคม','กุมภาพันธ์','มีนาคม','เมษายน','พฤษภาคม','มิถุนายน',
    'กรกฎาคม','สิงหาคม','กันยายน','ตุลาคม','พฤศจิกายน','ธันวาคม'
];

// Input: "01 มกราคม 2568"
const ThaiDayInput = React.forwardRef(({ onClick, selectedDate, placeholderText, inputClassName }, ref) => {
    const display = selectedDate
        ? `${String(selectedDate.getDate()).padStart(2, '0')} ${THAI_MONTHS[selectedDate.getMonth()]} ${selectedDate.getFullYear() + 543}`
        : '';
    return (
        <input
            className={inputClassName || 'form-control text-center'}
            value={display}
            onClick={onClick}
            onChange={() => {}}
            ref={ref}
            readOnly
            placeholder={placeholderText}
        />
    );
});

// Input: "มกราคม 2568"
const ThaiMonthYearInput = React.forwardRef(({ onClick, selectedDate, inputClassName }, ref) => {
    const display = selectedDate
        ? `${THAI_MONTHS[selectedDate.getMonth()]} ${selectedDate.getFullYear() + 543}`
        : '';
    return (
        <input
            className={inputClassName || 'form-control text-center'}
            value={display}
            onClick={onClick}
            onChange={() => {}}
            ref={ref}
            readOnly
        />
    );
});

// Header for standard day picker: "< มกราคม 2568 >"
const ThaiDayHeader = ({ date, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 10px' }}>
        <button
            type="button"
            className="react-datepicker__navigation react-datepicker__navigation--previous"
            onClick={decreaseMonth}
            disabled={prevMonthButtonDisabled}
            style={{ position: 'static' }}
        >{'<'}</button>
        <span className="react-datepicker__current-month">
            {THAI_MONTHS[date.getMonth()]} {date.getFullYear() + 543}
        </span>
        <button
            type="button"
            className="react-datepicker__navigation react-datepicker__navigation--next"
            onClick={increaseMonth}
            disabled={nextMonthButtonDisabled}
            style={{ position: 'static' }}
        >{'>'}</button>
    </div>
);

// Header for month-year picker: "< 2568 >"
const ThaiYearHeader = ({ date, decreaseYear, increaseYear, prevYearButtonDisabled, nextYearButtonDisabled }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 10px' }}>
        <button
            type="button"
            className="react-datepicker__navigation react-datepicker__navigation--previous"
            onClick={decreaseYear}
            disabled={prevYearButtonDisabled}
            style={{ position: 'static' }}
        >{'<'}</button>
        <span className="react-datepicker__current-month">
            {date.getFullYear() + 543}
        </span>
        <button
            type="button"
            className="react-datepicker__navigation react-datepicker__navigation--next"
            onClick={increaseYear}
            disabled={nextYearButtonDisabled}
            style={{ position: 'static' }}
        >{'>'}</button>
    </div>
);

// Header with month+year dropdowns in Thai/BE (for birth date pickers)
const ThaiDropdownHeader = ({ date, changeYear, changeMonth, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled, minDate, maxDate }) => {
    const currentYear = date.getFullYear();
    const minYear = minDate ? minDate.getFullYear() : currentYear - 100;
    const maxYear = maxDate ? maxDate.getFullYear() : new Date().getFullYear() + 1;
    const years = Array.from({ length: maxYear - minYear + 1 }, (_, i) => minYear + i);
    return (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 8px' }}>
            <button type="button" onClick={decreaseMonth} disabled={prevMonthButtonDisabled}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px' }}>{'<'}</button>
            <div style={{ display: 'flex', gap: '4px' }}>
                <select value={date.getMonth()} onChange={(e) => changeMonth(Number(e.target.value))} style={{ fontSize: '13px', padding: '2px' }}>
                    {THAI_MONTHS.map((m, i) => <option key={i} value={i}>{m}</option>)}
                </select>
                <select value={currentYear} onChange={(e) => changeYear(Number(e.target.value))} style={{ fontSize: '13px', padding: '2px' }}>
                    {years.map(y => <option key={y} value={y}>{y + 543}</option>)}
                </select>
            </div>
            <button type="button" onClick={increaseMonth} disabled={nextMonthButtonDisabled}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px' }}>{'>'}</button>
        </div>
    );
};

const ThaiDatePicker = ({ selected, showMonthYearPicker, showYearDropdown, showMonthDropdown, placeholderText, className, minDate, maxDate, ...rest }) => {
    const inputClass = className || 'form-control text-center';

    if (showMonthYearPicker) {
        return (
            <DatePicker
                {...rest}
                selected={selected}
                locale="th"
                showMonthYearPicker
                minDate={minDate}
                maxDate={maxDate}
                customInput={<ThaiMonthYearInput selectedDate={selected} inputClassName={inputClass} />}
                renderCustomHeader={(props) => <ThaiYearHeader {...props} />}
            />
        );
    }

    if (showYearDropdown || showMonthDropdown) {
        return (
            <DatePicker
                {...rest}
                selected={selected}
                locale="th"
                minDate={minDate}
                maxDate={maxDate}
                customInput={<ThaiDayInput selectedDate={selected} placeholderText={placeholderText} inputClassName={inputClass} />}
                renderCustomHeader={(props) => <ThaiDropdownHeader {...props} minDate={minDate} maxDate={maxDate} />}
            />
        );
    }

    return (
        <DatePicker
            {...rest}
            selected={selected}
            locale="th"
            minDate={minDate}
            maxDate={maxDate}
            placeholderText={placeholderText}
            customInput={<ThaiDayInput selectedDate={selected} placeholderText={placeholderText} inputClassName={inputClass} />}
            renderCustomHeader={(props) => <ThaiDayHeader {...props} />}
        />
    );
};

export default ThaiDatePicker;
